import React, { useCallback } from 'react';
import { ThumbsUp, ThumbsDown, Copy, DotsThreeVertical } from '@phosphor-icons/react';

import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { BlockRenderer } from './blocks';
import { ConversationStatus } from './ConversationStatus';
import { useConversation } from './ConversationContext';
import type { Message } from './types';

interface ConversationMessageAssistantProps {
  message: Message;
  className?: string;
}

export const ConversationMessageAssistant = ({ message, className }: ConversationMessageAssistantProps) => {
  const { callbacks } = useConversation();
  const isStreaming = message.status === 'streaming';
  const isPending = message.status === 'pending';
  const isCompleted = message.status === 'completed';

  const handleCopy = useCallback(() => {
    const textContent = message.content
      .filter((b): b is { type: 'text'; text: string } => b.type === 'text')
      .map((b) => b.text)
      .join('\n');
    callbacks.onCopy?.(message.id, textContent);
  }, [message, callbacks]);

  const hasContent = message.content.some((block) => block.text.length > 0);

  if ((isPending && message.content.length === 0) || (isStreaming && !hasContent)) {
    return <ConversationStatus className={className} />;
  }

  return (
    <div className={cn('flex gap-3 p-4 bg-surface rounded', className)}>
      <div className="flex-1 min-w-0">
        <div className="space-y-1">
          {message.content.map((block, i) => (
            <BlockRenderer
              key={i}
              block={block}
              isStreaming={isStreaming}
              onCitationClick={callbacks.onCitationClick}
            />
          ))}
        </div>

        {isCompleted && (
          <div className="mt-3 flex items-center gap-1">
            <ActionBarButton
              tooltip="Helpful"
              active={message.feedback === 'positive'}
              onClick={() => callbacks.onFeedback?.(message.id, 'positive')}
            >
              <ThumbsUp className="h-4 w-4" weight={message.feedback === 'positive' ? 'fill' : 'regular'} />
            </ActionBarButton>
            <ActionBarButton
              tooltip="Not helpful"
              active={message.feedback === 'negative'}
              onClick={() => callbacks.onFeedback?.(message.id, 'negative')}
            >
              <ThumbsDown className="h-4 w-4" weight={message.feedback === 'negative' ? 'fill' : 'regular'} />
            </ActionBarButton>
            <ActionBarButton tooltip="Copy" onClick={handleCopy}>
              <Copy className="h-4 w-4" />
            </ActionBarButton>
            <ActionBarButton tooltip="More">
              <DotsThreeVertical className="h-4 w-4" />
            </ActionBarButton>
          </div>
        )}
      </div>
    </div>
  );
};

interface ActionBarButtonProps {
  children: React.ReactNode;
  tooltip: string;
  active?: boolean;
  onClick?: () => void;
}

const ActionBarButton = ({ children, tooltip, active, onClick }: ActionBarButtonProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            active && 'text-primary',
          )}
          onClick={onClick}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};
